import { Row } from "@tanstack/react-table";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { getFinalChildrenTableConfig } from "PFComponents/table/parts/nested_table_row.utils";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";

import { ChildrenTableConfig, NestedTableData } from "../nested_table";
import Table, { TableData, TableProps } from "../table";
import css from "../table.module.scss";
import { OnRowClickFn } from "../table.types";
import { TableCell } from "./table_cell";

type NestedTableRowProps<
  CHILDREN_TABLE_DATA extends TableData,
  TABLE_DATA extends NestedTableData<CHILDREN_TABLE_DATA>
> = {
  row: Row<TABLE_DATA>;
  onRowClick?: OnRowClickFn<TABLE_DATA>;
  compact?: boolean;
  noHover?: boolean;
  lastRowRef?: React.RefObject<HTMLTableRowElement | null>;
  childrenTableConfig: ChildrenTableConfig<CHILDREN_TABLE_DATA>;
} & Pick<TableProps<TABLE_DATA>, "getRowId">;

export const NestedTableRow = <
  CHILDREN_TABLE_DATA extends TableData,
  TABLE_DATA extends NestedTableData<CHILDREN_TABLE_DATA>
>({
  row,
  onRowClick,
  compact,
  noHover,
  lastRowRef,
  childrenTableConfig,
  getRowId
}: NestedTableRowProps<CHILDREN_TABLE_DATA, TABLE_DATA>) => {
  const { disabled, message, childrenTableData, childrenTableSorting } = row.original;
  const { childrenPaginationOptions } = childrenTableConfig;

  const rowId = getRowId?.(row.original, row.index) ?? String(row.index);
  const paginationOptions = childrenPaginationOptions
    ? {
        currentPage: childrenPaginationOptions.getCurrentPage(rowId),
        totalPages: childrenPaginationOptions.getTotalPages(rowId),
        onClick: (page: number) => childrenPaginationOptions.onClick(rowId, page),
        className: childrenPaginationOptions.className
      }
    : undefined;

  const isExpanded = row.getIsExpanded();

  const finalChildrenTableConfig = getFinalChildrenTableConfig({
    rowId,
    childrenTableConfig,
    childrenTableData,
    childrenTableSorting
  });

  const handleRowClick = () => {
    if (onRowClick) {
      onRowClick(row.original);
    }
  };

  const isNestedTableEmpty = isEmpty(finalChildrenTableConfig.tableData);
  const isNestedTableLoading = finalChildrenTableConfig.getIsLoading?.(rowId);
  const isNestedTableInitialLoading = isNestedTableEmpty && isNestedTableLoading;
  const isNoDataMessageDisplayed =
    !!childrenTableConfig.noDataMessage && isNestedTableEmpty && !isNestedTableLoading;
  const isNestedTableVisible = !childrenTableConfig.noDataMessage || !isNestedTableEmpty;

  return (
    <>
      <Tooltip
        content={
          <Typography variant="bodyRegular" tag="span">
            {message}
          </Typography>
        }
        disabled={!message}
        placement="top-start"
        triggerRef={lastRowRef}
      >
        {({ ref, getReferenceProps }) => (
          <tr
            ref={ref}
            {...getReferenceProps({
              className: classNames(css.row, css.rowBody, {
                [css.semitransparent]: disabled,
                [css.noHover]: noHover,
                [css.expanded]: isExpanded
              }),
              style: { cursor: onRowClick ? "pointer" : "default" },
              onClick: handleRowClick
            })}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id} cell={cell} compact={compact} />
            ))}
          </tr>
        )}
      </Tooltip>

      {isExpanded && (
        <tr className={css.nestedRow}>
          <td
            colSpan={row.getVisibleCells().length}
            style={{
              height: "inherit",
              padding: "inherit"
            }}
          >
            {isNestedTableInitialLoading && (
              <Inline className={css.nestedTableEmptyWrapper}>
                <LoadingDots circlesEnabled circleSize="sm" />
              </Inline>
            )}
            {isNoDataMessageDisplayed && (
              <Inline alignItems="center" className={css.nestedTableEmptyWrapper}>
                <Icon name="missing" size="md" color="paletteBlue2" />
                <Typography variant="bodyRegular">{childrenTableConfig.noDataMessage}</Typography>
              </Inline>
            )}
            {isNestedTableVisible && (
              <Table<CHILDREN_TABLE_DATA>
                {...finalChildrenTableConfig}
                paginationOptions={paginationOptions}
                isNestedTable
                className={classNames(css.nestedTable, childrenTableConfig.className)}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
};
